import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarPlus,
  CheckCheck,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  Hand,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Smartphone,
  Stethoscope,
  UserPlus,
} from "lucide-react";
import { AppShell } from "../components/AppShell";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { api, ApiException } from "../lib/api";
import { cn } from "../lib/cn";

interface MeResp {
  user: { id: string; email: string; name: string | null } | null;
  circles: Array<{ id: string; name: string; role: "admin" | "member"; loved_one_name: string | null }>;
}

export function Help() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResp | null>(null);

  useEffect(() => {
    api
      .get<MeResp>("/api/me")
      .then(setMe)
      .catch((err) => {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      });
  }, [navigate]);

  const isAdmin = me?.circles[0]?.role === "admin";
  const lovedOne = me?.circles[0]?.loved_one_name ?? "your loved one";

  return (
    <AppShell activeTab="help">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-10">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary-soft border border-primary/15 flex items-center justify-center">
            <CircleHelp className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Help</h1>
            <p className="text-sm text-text-muted">
              Quick answers for everyone in {me?.circles[0]?.name ?? "your circle"}.
              {isAdmin && <Badge tone="info" className="ml-2 align-middle">Admin view</Badge>}
            </p>
          </div>
        </div>

        {/* What is this? */}
        <Section title="What is Carol Care?" icon={<Stethoscope className="h-4 w-4" />}>
          <p>
            Carol Care is a family-only app for coordinating <strong>{lovedOne}</strong>'s medical visits.
            One person can post an appointment; the whole family gets notified by email (and optionally SMS);
            one person accepts; everyone else stays in the loop.
          </p>
        </Section>

        {/* For everyone */}
        <SectionGroup title="For everyone (members + admins)">
          <FaqCard
            icon={<Hand className="h-4 w-4 text-primary" />}
            title="Accepting a visit"
            body={
              <>
                <p>When a new visit is posted, you'll get an email. Open it and tap <strong>Accept this visit</strong>.</p>
                <p className="mt-2">Two things happen:</p>
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  <li>The whole family is told you're covering it.</li>
                  <li>You get a personal confirmation email with a calendar invite (.ics) attached. Open it on your phone or computer and it adds the appointment to your calendar with an alarm at the "arrive by" time.</li>
                </ul>
                <p className="mt-2 text-text-muted">First tap wins — if two people accept at the same second, only one succeeds; the other sees who took it.</p>
              </>
            }
          />

          <FaqCard
            icon={<CheckCheck className="h-4 w-4 text-primary" />}
            title="Assisting someone else"
            body={
              <>
                <p>After a primary acceptor has it, you'll see an <strong>Assist {`{name}`}</strong> button on that visit. Tap it if you want to come along — extra hands at doctor visits help a lot.</p>
                <p className="mt-2">The primary acceptor (and admins) get a heads-up that you're joining. Everyone else doesn't get an email — quiet by design.</p>
              </>
            }
          />

          <FaqCard
            icon={<CircleHelp className="h-4 w-4 text-warning" />}
            title="Declining a visit"
            body={
              <>
                <p>If you can't cover one, tap <strong>Decline</strong> and add a short reason ("traveling that week", "scheduling conflict"). Only the admins see your reason — the rest of the family doesn't, to keep noise down. The visit stays open for someone else.</p>
              </>
            }
          />

          <FaqCard
            icon={<Phone className="h-4 w-4 text-primary" />}
            title="Getting to the appointment"
            body={
              <>
                <p>On the visit's detail page you'll see:</p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li><strong>Arrive by</strong> time (15–30 minutes earlier than the start) — that's your real target.</li>
                  <li><strong>Map</strong> opens directions in your phone's maps app.</li>
                  <li><strong>Call</strong> dials the clinic phone directly.</li>
                  <li><strong>Instructions from clinic</strong> — anything they asked you to bring, fasting requirements, etc.</li>
                  <li><strong>Family notes</strong> — context the family knows that the clinic doesn't.</li>
                </ul>
              </>
            }
          />

          <FaqCard
            icon={<MessageSquareText className="h-4 w-4 text-primary" />}
            title="Post-visit notes"
            body={
              <>
                <p>After the appointment, the acceptor (or any admin) can add notes — how it went, follow-up needed, prescription details. Save them on the visit page so the family has a record.</p>
              </>
            }
          />

          <FaqCard
            icon={<Bell className="h-4 w-4 text-primary" />}
            title="Email + optional SMS"
            body={
              <>
                <p>Email is on by default. To also get text messages, go to <strong>Alerts</strong> in the nav, enter your mobile + carrier, and pick which events should ping your phone.</p>
                <p className="mt-2">The most useful one is the <strong>24-hour escalation</strong> — when a visit's coming up and nobody has accepted yet. Worth keeping that on even if you turn the others off.</p>
              </>
            }
          />

          <FaqCard
            icon={<ClipboardList className="h-4 w-4 text-primary" />}
            title="Proposing a visit (members)"
            body={
              <>
                <p>You don't have to wait for an admin to know about an appointment. Tap <strong>+ Propose visit</strong> anywhere you see it — fill in the same form as admins do.</p>
                <p className="mt-2">When you submit, your proposal is held for admin review:</p>
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  <li>The family is <em>not</em> notified yet.</li>
                  <li>Admins get an email asking them to approve.</li>
                  <li>The visit appears in everyone's list marked <strong>Pending</strong>.</li>
                </ul>
                <p className="mt-2">You'll get a separate email when an admin approves (the family is then notified automatically) or rejects, with their reason. You can also propose a new provider this way — pick <strong>+ Add new provider</strong> inline while creating the visit.</p>
              </>
            }
          />

          <FaqCard
            icon={<Smartphone className="h-4 w-4 text-primary" />}
            title="Add to home screen"
            body={
              <>
                <p>Carol Care works like a real app once installed:</p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li><strong>iPhone:</strong> Safari → Share → Add to Home Screen.</li>
                  <li><strong>Android:</strong> Chrome usually offers an install prompt automatically. If not, menu → Add to Home Screen.</li>
                </ul>
                <p className="mt-2 text-text-muted">When signing in from the home-screen app, you can either tap the link in your email <strong>or</strong> just type the 6-digit code on the sign-in screen. Both work.</p>
              </>
            }
          />
        </SectionGroup>

        {/* Admin section */}
        {isAdmin && (
          <SectionGroup title="For admins">
            <FaqCard
              icon={<CalendarPlus className="h-4 w-4 text-primary" />}
              title="Posting a visit"
              body={
                <>
                  <p>From the home screen or the Visits tab, tap <strong>+ New visit</strong>. The form:</p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li>Pick a provider you've added before, or tap <strong>+ Add new provider</strong> in line.</li>
                    <li>Pick the start time (in Central Time).</li>
                    <li>Choose how early to arrive (default 15 min).</li>
                    <li>Toggle <strong>Ride required</strong> if {lovedOne} needs a pickup; add estimated travel time.</li>
                    <li>Optionally add clinic instructions (e.g., "fast 8 hours before") and family notes.</li>
                  </ul>
                  <p className="mt-2">Hit <strong>Post visit</strong> — the family is emailed immediately.</p>
                </>
              }
            />

            <FaqCard
              icon={<ClipboardCheck className="h-4 w-4 text-primary" />}
              title="Reviewing member proposals"
              body={
                <>
                  <p>When a family member proposes a visit or a provider, you get an email and the item appears flagged <strong>Pending</strong> in the app. The family hasn't been notified yet — that happens when you approve.</p>
                  <p className="mt-2">On a pending visit's detail page you'll see a yellow banner with two actions:</p>
                  <ul className="mt-1 list-disc pl-5 space-y-1">
                    <li><strong>Approve & notify family</strong> — fires the normal "new visit" broadcast (email + SMS to opted-in members). The proposer gets a personal "approved" email.</li>
                    <li><strong>Reject</strong> — deletes the proposal. You can include an optional reason that goes to the proposer only.</li>
                  </ul>
                  <p className="mt-2">For pending providers, the same Approve / Reject buttons appear in <strong>Settings → Providers</strong> at the top of the list.</p>
                  <p className="mt-2 text-xs text-text-faint">Tip: if a member proposes a visit using a brand-new provider, approving the visit also approves the provider automatically.</p>
                </>
              }
            />

            <FaqCard
              icon={<Stethoscope className="h-4 w-4 text-primary" />}
              title="Managing providers"
              body={
                <>
                  <p>Settings → Providers. Add a doctor once, reuse them forever. Editing a provider does <strong>not</strong> change details on past visits — they're snapshotted at the moment you create the visit. Archiving hides them from new visits but keeps them readable on historical ones.</p>
                </>
              }
            />

            <FaqCard
              icon={<UserPlus className="h-4 w-4 text-primary" />}
              title="Adding family members"
              body={
                <>
                  <p>Settings → Members → <strong>Invite</strong>. Pick member or admin. They get an email with a one-click join link that signs them in automatically. The last admin can't be demoted or removed — promote someone else first.</p>
                </>
              }
            />

            <FaqCard
              icon={<ShieldCheck className="h-4 w-4 text-primary" />}
              title="Reassigning or cancelling"
              body={
                <>
                  <p>On any visit's detail page:</p>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    <li><strong>Cancel visit</strong> — the family is notified.</li>
                    <li>Manually assign (Phase 4) — if nobody steps up after the 24h urgent ping, you can pick someone yourself; they get an explicit "you've been assigned" email.</li>
                  </ul>
                </>
              }
            />
          </SectionGroup>
        )}

        {/* Contact + support */}
        <Section title="Need help or have feedback?" icon={<CircleHelp className="h-4 w-4" />}>
          <p>
            Email <a href="mailto:support@carolcare.io" className="text-primary font-medium hover:underline">support@carolcare.io</a> — it goes straight to John's inbox.
            Include a screenshot of the visit or page you're looking at if something's off, and the email address you're signed in with. He usually replies within a day.
          </p>
          <p className="mt-3 text-xs text-text-faint">
            Note: <code>care@carolcare.io</code> is the send-only address our automatic emails come from. Replies to it bounce. Always use <code>support@carolcare.io</code> for actual help.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href="mailto:support@carolcare.io?subject=Carol%20Care%20help">
              <button type="button" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg h-11 px-4 text-[15px] bg-primary text-primary-fg hover:bg-primary-hover">
                Email support
              </button>
            </a>
            <a href="/support">
              <button type="button" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg h-11 px-4 text-[15px] border border-border bg-surface text-text hover:bg-surface-2">
                Buy John a coffee
              </button>
            </a>
          </div>
        </Section>
      </div>
    </AppShell>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight mb-2">
        {icon} {title}
      </h2>
      <Card>
        <CardBody className="text-sm leading-relaxed text-text-muted">
          {children}
        </CardBody>
      </Card>
    </section>
  );
}

function SectionGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-faint mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function FaqCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: React.ReactNode }) {
  return (
    <Card interactive className={cn("transition-colors hover:border-primary/30")}>
      <CardBody className="!p-4 sm:!p-5">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="h-7 w-7 rounded-lg bg-primary-soft flex items-center justify-center shrink-0">{icon}</div>
          <h3 className="font-semibold">{title}</h3>
        </div>
        <div className="text-sm leading-relaxed text-text-muted">{body}</div>
      </CardBody>
    </Card>
  );
}
